/**
 * writing-style-draft — the W4 Schreibstil track's LOCAL, per-user draft of everything the
 * person has answered but not yet submitted.
 *
 * W4 is the app's longest single-page work session, and until this it was the least protected:
 * all three rounds hold their answers client-side until ONE submit, and the free texts — the
 * highest-signal artifacts in the workshop — lived only in the DOM textareas.
 *
 *   ① »prose«      one sanctioned free text, written from scratch (DOM only).
 *   ② »scenarios«  the whole SET at once — a pole pick, a fit rating and an optional rewrite
 *                  per situation, batched into a single `/verdicts` POST by design
 *                  (»question SETS per round, auto-advance«). Every scenario the person has
 *                  worked through is unsaved until the last one is settled.
 *   ③ »validation« the generated Schreibstil prose, loaded INTO an editor for the person to
 *                  rewrite before »speichern« (DOM only).
 *
 * The surface's own note said the rewrites were safe because "every scenario stays mounted, so
 * nothing can be lost by a re-render" — true, and beside the point: what ends the page's life
 * here is a reload or the data-plane 401 route-to-login round-trip (`services.ts`), which takes
 * the whole DOM with it. So the track mirrors its answers here on every mutation, restores them
 * on mount, and drops a round's slice once that round's POST makes it durable.
 *
 * The storage plumbing and the untrusted-boundary discipline live in `local-draft.ts`; this
 * module owns the shape and its structural guard.
 */

import type { WritingScenarioVerdict } from "@career/definition-v2"
import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** The fit vocabulary, straight off the domain type — never re-spelled (same as the surface). */
type WritingFit = WritingScenarioVerdict["fit"]

/** One situation's in-progress answer: the chosen pole, the fit rating, the optional rewrite. */
export interface ScenarioAnswerDraft {
  /** The chosen pole, or null while the person has not decided. */
  choiceId: string | null
  /** The fit rating, or null while unrated. */
  fit: WritingFit | null
  /** The person's own rewrite (optional by design — empty means they skipped it). */
  rewrite: string
}

/** The whole track's unsaved state across all three rounds. */
export interface WritingStyleDraft {
  /** Round ① — the free text as typed so far. */
  prose: string
  /** Round ② — one entry per situation the person has touched, keyed by scenario id. */
  scenarios: Record<string, ScenarioAnswerDraft>
  /** Round ③ — the in-progress rewrite of the proposal; null while the editor is closed. */
  edit: string | null
  /** When this draft was last touched (ISO-8601) — the freshness clock. */
  updatedAt: string
}

/** The fit answers, mirrored as a runtime set for the structural guard. */
const FITS = new Set<string>(["trifft", "fast", "daneben"])

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

/**
 * One situation's answer. Every field may legitimately be empty mid-round — an unrated
 * situation, a skipped rewrite, a pick the person took back — so the guard checks TYPE and
 * vocabulary, never presence. Requiring a complete answer would discard the exact drafts
 * worth rescuing: the half-finished ones.
 */
function isScenarioAnswerDraft(v: unknown): v is ScenarioAnswerDraft {
  if (!isRecord(v)) return false
  const { choiceId, fit, rewrite } = v
  return (
    (choiceId === null || typeof choiceId === "string") &&
    (fit === null || (typeof fit === "string" && FITS.has(fit))) &&
    typeof rewrite === "string"
  )
}

function isScenarioMap(v: unknown): v is Record<string, ScenarioAnswerDraft> {
  return isRecord(v) && Object.values(v).every(isScenarioAnswerDraft)
}

/**
 * A total, dependency-free guard for a persisted track draft. Structural only: it ensures the
 * shape is sound enough to seed the controls and to hand to the round's POST, which
 * re-validates against the real domain validators server-side.
 */
export function isWritingStyleDraft(v: unknown): v is WritingStyleDraft {
  if (!isRecord(v)) return false
  return (
    typeof v["prose"] === "string" &&
    isScenarioMap(v["scenarios"]) &&
    (v["edit"] === null || typeof v["edit"] === "string") &&
    typeof v["updatedAt"] === "string"
  )
}

/** The draft an untouched track starts from. */
export const emptyWritingStyleDraft = (): WritingStyleDraft => ({
  prose: "",
  scenarios: {},
  edit: null,
  updatedAt: new Date(0).toISOString(),
})

/** True when the draft holds nothing worth restoring — used to avoid writing empty slots. */
export function isEmptyWritingStyleDraft({ prose, scenarios, edit }: WritingStyleDraft): boolean {
  if (prose.length > 0 || (edit !== null && edit.length > 0)) return false
  return Object.values(scenarios).every((a) => a.choiceId === null && a.fit === null && a.rewrite.length === 0)
}

export type WritingStyleDraftStore = LocalDraftStore<WritingStyleDraft>

const KEY_PREFIX = "career:writing-style:draft:v1:"

/** A per-user track-draft store over localStorage, guarded by {@link isWritingStyleDraft}. */
export function createWritingStyleDraftStore(userId: string | null, explicitStorage?: Storage): WritingStyleDraftStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isWritingStyleDraft, explicitStorage)
}
